/**
 * view.ts — ข้อมูลรายงานที่หน้าจอใช้ (demo หรือ live จาก API) ตามตัวกรองปัจจุบัน
 * ทุกหน้าจอ/กราฟ/export อ่านจากที่เดียวกัน → ตัวเลขตรงกันเสมอ (T077/T083)
 */
import { buildPnlReport, buildDailySeries, type PnlLine, type PnlLineKey } from '@rrp/shared';
import { demoCurrent, demoPrevMonth } from './demo-data.js';
import { drilldownFor as demoDrill, type DrillRow, APR, MAR } from './report.js';
import type { PnlPayload } from './api.js';

export interface DailyPoint {
  date: string;
  sales: string;
}

export interface ReportView {
  mode: 'demo' | 'live';
  range: { from: string; to: string };
  compareRange: { from: string; to: string } | null;
  lines: PnlLine[];
  completeness: { status: string; missingCostCount: number; affectedRevenue: string };
  prevAmounts: Partial<Record<PnlLineKey, number>>;
  daily: DailyPoint[];
  channelMix: { channelCode: string; sales: string }[];
  drilldown(key: PnlLineKey): DrillRow[];
}

function amounts(lines: PnlLine[]): Partial<Record<PnlLineKey, number>> {
  const m: Partial<Record<PnlLineKey, number>> = {};
  for (const l of lines) if (l.amount !== null) m[l.key] = Number(l.amount);
  return m;
}

export function demoView(range = APR): ReportView {
  const cur = buildPnlReport(demoCurrent, range);
  const prev = buildPnlReport(demoPrevMonth, MAR);
  return {
    mode: 'demo',
    range,
    compareRange: MAR,
    lines: cur.lines,
    completeness: cur.completeness,
    prevAmounts: amounts(prev.lines),
    daily: buildDailySeries({ sales: demoCurrent.sales, refunds: demoCurrent.refunds }, range),
    channelMix: [
      { channelCode: 'tiktok_shop', sales: '260000' },
      { channelCode: 'shopee', sales: '120000' },
      { channelCode: 'lazada', sales: '35000' },
      { channelCode: 'facebook', sales: '12000' },
      { channelCode: 'line', sales: '5600' },
    ],
    drilldown: demoDrill,
  };
}

export function liveView(input: {
  current: PnlPayload;
  comparison: PnlPayload | null;
  range: { from: string; to: string };
  compareRange: { from: string; to: string } | null;
  daily: DailyPoint[];
  channelMix: { channelCode: string; sales: string }[];
}): ReportView {
  return {
    mode: 'live',
    range: input.range,
    compareRange: input.compareRange,
    lines: input.current.lines,
    completeness: input.current.completeness,
    prevAmounts: input.comparison ? amounts(input.comparison.lines) : {},
    daily: input.daily,
    channelMix: input.channelMix,
    drilldown: () => [], // live ใช้ /api/pnl/drilldown (ดู pages.ts)
  };
}

let _view: ReportView = demoView();
export function getView(): ReportView {
  return _view;
}
export function setView(v: ReportView): void {
  _view = v;
}
